'use client'

import { ContentImage } from '@/components/ui/content-image'
import { StepperButton } from '@/components/ui/stepper-button'
import { useTapLock } from '@/hooks/use-tap-lock'
import { useAppDispatch } from '@/lib/store'
import { addToCart, openCartDrawer } from '@/lib/store/features/cart/cartSlice'
import { IProductVariant } from '@/lib/types/core/product-variant'
import { ProductItem } from '@/lib/types/operations/product'
import {
  calculateLineTotal,
  resolvePrice,
  resolveRollSavingsPercent
} from '@/lib/utils/price-resolution'
import { showToast, ToastSuppress } from '@/lib/utils/toast'
import {
  convertPricePerUnit,
  convertUnit,
  formatUnit,
  snapToStep,
  SWITCHABLE_UNITS
} from '@/lib/utils/unit-conversion'
import { UnitOfMeasure } from '@prisma/client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

type PurchaseMode = 'custom-cut' | 'full-roll'
type DisplayUnit = (typeof SWITCHABLE_UNITS)[number]

interface AddToCartModalProps {
  isOpen: boolean
  onClose: () => void
  product: ProductItem
  variant?: IProductVariant | null
  priceTier: string
}

function getStockQuantity(
  product: ProductItem,
  variant?: IProductVariant | null
): number {
  if (variant) {
    // Check variant inventory
    if (variant.inventory && variant.inventory.length > 0) {
      return variant.inventory.reduce(
        (sum, inv) => sum + (Number(inv.quantity) || 0),
        0
      )
    }
    // Check product inventory items for this variant
    if (product.inventoryItems && product.inventoryItems.length > 0) {
      const variantItems = product.inventoryItems.filter(
        (item) => item.variantId === variant.id
      )
      if (variantItems.length > 0) {
        return variantItems.reduce(
          (sum, item) => sum + (Number(item.quantity) || 0),
          0
        )
      }
    }
  }

  // Fallback to product-level inventory
  let total = 0
  if (product.inventoryItems && product.inventoryItems.length > 0) {
    total = product.inventoryItems.reduce(
      (sum, item) => sum + (Number(item.quantity) || 0),
      0
    )
  }
  if (product.inventory?.quantity) {
    total += Number(product.inventory.quantity) || 0
  }
  return total
}

export function AddToCartModal({
  isOpen,
  onClose,
  product,
  variant,
  priceTier
}: AddToCartModalProps) {
  const dispatch = useAppDispatch()
  const backdropRef = useRef<HTMLDivElement>(null)
  // Synchronous lock: a rapid double-tap must not double the cart quantity
  const acquireTapLock = useTapLock()

  // State
  const [purchaseMode, setPurchaseMode] = useState<PurchaseMode>('custom-cut')
  const [displayUnit, setDisplayUnit] = useState<DisplayUnit>('FOOT')
  const [lengthInput, setLengthInput] = useState('3')
  const [pieces, setPieces] = useState(1)
  const [rollQuantity, setRollQuantity] = useState(1)
  const [note, setNote] = useState('')
  const [lengthError, setLengthError] = useState<string | null>(null)

  // Derived data
  const nativeUnit: string = variant?.unitOfMeasure ?? product.unitOfMeasure ?? 'FOOT'

  const variantPricing = useMemo(() => {
    if (!variant) return null
    // retailPrice/wholesalePrice are the real DB columns; pricePerUnit
    // (attributeValues-derived) is only a fallback for variants seeded
    // before those columns existed.
    return {
      retailPrice: variant.retailPrice ?? variant.pricePerUnit ?? null,
      wholesalePrice: variant.wholesalePrice ?? null,
      rollPrice: (variant.rollPrice as number) ?? null,
      rollLengthUnits: (variant.rollLengthUnits as number) ?? null
    }
  }, [variant])

  const pricePerNativeUnit = useMemo(() => {
    if (!variantPricing) return 0
    return resolvePrice(variantPricing, priceTier as 'RETAIL' | 'WHOLESALE') ?? 0
  }, [variantPricing, priceTier])

  const pricePerDisplayUnit = useMemo(() => {
    return convertPricePerUnit(pricePerNativeUnit, nativeUnit, displayUnit)
  }, [pricePerNativeUnit, nativeUnit, displayUnit])

  const hasRollOption = useMemo(() => {
    if (!variantPricing) return false
    return (
      variantPricing.rollPrice !== null &&
      variantPricing.rollPrice > 0 &&
      variantPricing.rollLengthUnits !== null &&
      variantPricing.rollLengthUnits > 0
    )
  }, [variantPricing])

  const rollSavingsPercent = useMemo(() => {
    if (!variantPricing) return null
    return resolveRollSavingsPercent(
      variantPricing,
      priceTier as 'RETAIL' | 'WHOLESALE'
    )
  }, [variantPricing, priceTier])

  const stockQuantity = useMemo(
    () => getStockQuantity(product, variant),
    [product, variant]
  )
  const isOutOfStock = stockQuantity <= 0

  const lengthValue = parseFloat(lengthInput) || 0

  // Convert length back to native units for validation/cart
  const lengthInNativeUnits = useMemo(() => {
    return convertUnit(lengthValue, displayUnit, nativeUnit)
  }, [lengthValue, displayUnit, nativeUnit])

  // Validation
  const minOrder = variant?.minOrderQuantity ?? product.minOrderQuantity ?? 1
  const maxOrder =
    variant?.maxOrderQuantity ?? product.maxOrderQuantity ?? null
  const incrementStep = variant?.incrementStep ?? product.incrementStep ?? 1

  const validateLength = useCallback(
    (val: number): string | null => {
      if (val <= 0) return 'Length must be greater than 0'
      const nativeLen = convertUnit(val, displayUnit, nativeUnit)
      if (nativeLen < minOrder) {
        const minInDisplay = convertUnit(minOrder, nativeUnit, displayUnit)
        return `Minimum ${minInDisplay.toFixed(1)} ${formatUnit(displayUnit)}`
      }
      if (maxOrder && nativeLen > maxOrder) {
        const maxInDisplay = convertUnit(maxOrder, nativeUnit, displayUnit)
        return `Maximum ${maxInDisplay.toFixed(1)} ${formatUnit(displayUnit)}`
      }
      // Check stock
      if (nativeLen * pieces > stockQuantity && stockQuantity > 0) {
        return `Only ${stockQuantity} ${formatUnit(nativeUnit)} available`
      }
      return null
    },
    [displayUnit, nativeUnit, minOrder, maxOrder, pieces, stockQuantity]
  )

  // Compute total
  const total = useMemo(() => {
    if (purchaseMode === 'full-roll' && variantPricing?.rollPrice) {
      return calculateLineTotal({
        purchaseMode: 'full-roll',
        quantity: rollQuantity,
        rollPrice: variantPricing.rollPrice
      })
    }
    if (purchaseMode === 'custom-cut' && pricePerNativeUnit > 0) {
      return calculateLineTotal({
        purchaseMode: 'custom-cut',
        lengthPerPiece: lengthInNativeUnits,
        pieces,
        pricePerUnit: pricePerNativeUnit
      })
    }
    return 0
  }, [
    purchaseMode,
    variantPricing,
    rollQuantity,
    pricePerNativeUnit,
    lengthInNativeUnits,
    pieces
  ])

  // Snap length on blur
  const handleLengthBlur = useCallback(() => {
    if (incrementStep > 0 && lengthValue > 0) {
      const snappedNative = snapToStep(
        convertUnit(lengthValue, displayUnit, nativeUnit),
        incrementStep
      )
      const snappedDisplay = convertUnit(snappedNative, nativeUnit, displayUnit)
      setLengthInput(
        snappedDisplay % 1 === 0
          ? snappedDisplay.toString()
          : snappedDisplay.toFixed(2)
      )
    }
    const err = validateLength(lengthValue)
    setLengthError(err)
  }, [
    incrementStep,
    lengthValue,
    displayUnit,
    nativeUnit,
    validateLength
  ])

  // Step the length by one increment. The arithmetic happens in native units
  // so the result always lands on the same grid handleLengthBlur snaps to,
  // whatever unit the field is currently displaying.
  const stepLength = useCallback(
    (direction: 1 | -1) => {
      const stepNative = incrementStep > 0 ? incrementStep : 1
      const currentNative = convertUnit(lengthValue, displayUnit, nativeUnit)
      const nextNative = Math.max(
        minOrder,
        snapToStep(currentNative, stepNative) + direction * stepNative
      )
      const capped = maxOrder ? Math.min(nextNative, maxOrder) : nextNative
      const nextDisplay = convertUnit(capped, nativeUnit, displayUnit)
      setLengthInput(
        nextDisplay % 1 === 0 ? nextDisplay.toString() : nextDisplay.toFixed(2)
      )
      setLengthError(validateLength(nextDisplay))
    },
    [
      incrementStep,
      lengthValue,
      displayUnit,
      nativeUnit,
      minOrder,
      maxOrder,
      validateLength
    ]
  )

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPurchaseMode('custom-cut')
      setDisplayUnit('FOOT')
      setLengthInput('3')
      setPieces(1)
      setRollQuantity(1)
      setNote('')
      setLengthError(null)
    }
  }, [isOpen])

  // Close on escape
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Handle add to cart
  const handleAddToCart = useCallback(() => {
    if (isOutOfStock) return

    if (purchaseMode === 'custom-cut') {
      const err = validateLength(lengthValue)
      if (err) {
        setLengthError(err)
        return
      }
    }

    // Acquired after validation so a corrected retry is never blocked;
    // a second tap inside the lock window is a no-op
    if (!acquireTapLock()) return

    const payload: Parameters<typeof addToCart>[0] = {
      productId: product.id || '',
      product,
      quantity:
        purchaseMode === 'full-roll' ? rollQuantity : 1,
      variantId: variant?.id,
      purchaseMode,
      pieces: purchaseMode === 'full-roll' ? 1 : pieces,
      lengthPerPiece:
        purchaseMode === 'custom-cut' ? lengthInNativeUnits : undefined,
      note: note.trim() || undefined,
      unitOfMeasure:
        (variant?.unitOfMeasure as UnitOfMeasure) ??
        (product.unitOfMeasure as UnitOfMeasure) ??
        UnitOfMeasure.FOOT
    }

    dispatch(addToCart(payload))
    showToast({
      title: 'Added to cart',
      color: 'success',
      timeout: 3000,
      skipIf: ToastSuppress.onMobile
    })
    dispatch(openCartDrawer())
    onClose()
  }, [
    isOutOfStock,
    purchaseMode,
    validateLength,
    lengthValue,
    product,
    rollQuantity,
    variant,
    pieces,
    lengthInNativeUnits,
    note,
    dispatch,
    onClose,
    acquireTapLock
  ])

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === backdropRef.current) onClose()
    },
    [onClose]
  )

  if (!isOpen) return null

  const widthDisplay = variant?.widthInches ?? product.width
  const materialType =
    (product.attributes?.materialType as string) ?? ''
  const brandName =
    (product as ProductItem & { categoryName?: string }).categoryName ?? ''
  const rollLength = variantPricing?.rollLengthUnits
  const rollLengthInDisplay = rollLength
    ? convertUnit(rollLength, nativeUnit, displayUnit)
    : null

  // Renders through a portal to document.body, which sits inside the <html>
  // theme scope, so the portaled backdrop resolves the theme tokens without
  // a data-theme of its own.
  const modalContent = (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center sm:items-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Configure Your Order"
    >
      <div
        className={[
          'bg-overlay text-foreground w-full max-w-md overflow-y-auto shadow-xl',
          // Mobile: bottom sheet
          'fixed bottom-0 sm:relative sm:bottom-auto rounded-t-2xl sm:rounded-xl',
          'max-h-[90vh] sm:max-h-[85vh]'
        ].join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="cw-ui-heading text-base text-foreground">
            Configure Your Order
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted hover:text-foreground transition-colors p-1"
            aria-label="Close modal"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Product summary */}
        <div className="flex gap-3 px-5 py-4 border-b border-border">
          <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 bg-background">
            <ContentImage
              src={product.media?.[0]?.url}
              alt={product.name}
              contentType="PRODUCT"
              className="w-full h-full object-cover"
              fallbackSrc="/images/placeholder-product.jpg"
            />
          </div>
          <div className="flex flex-col justify-center min-w-0">
            {brandName && (
              <span className="text-[10px] font-semibold text-muted uppercase tracking-widest">
                {brandName}
              </span>
            )}
            <span className="text-sm font-bold text-foreground truncate">
              {product.name}
            </span>
            <span className="text-xs text-muted">
              {widthDisplay ? `${widthDisplay}" wide` : ''}
              {widthDisplay && materialType ? ' | ' : ''}
              {materialType ? String(materialType) : ''}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-4">
          {/* Purchase mode toggle — only show if roll option exists */}
          {hasRollOption && (
            <div className="flex rounded-lg overflow-hidden border border-border">
              <button
                type="button"
                onClick={() => setPurchaseMode('custom-cut')}
                className={[
                  'flex-1 py-2 text-xs font-semibold transition-colors',
                  purchaseMode === 'custom-cut'
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-transparent text-muted hover:text-foreground'
                ].join(' ')}
              >
                Custom Cut
              </button>
              <button
                type="button"
                onClick={() => setPurchaseMode('full-roll')}
                className={[
                  'flex-1 py-2 text-xs font-semibold transition-colors',
                  purchaseMode === 'full-roll'
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-transparent text-muted hover:text-foreground'
                ].join(' ')}
              >
                Full Roll
              </button>
            </div>
          )}

          {purchaseMode === 'custom-cut' && (
            <>
              {/* Length with unit switcher */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-muted uppercase tracking-wide">
                    Length
                  </label>
                  {/* A segmented control on a paper track. The inactive
                      buttons used to be bg-surface, which is white, so on the
                      white modal only the selected teal chip was visible and
                      the other two units read as loose letters. */}
                  <div className="inline-flex gap-0.5 rounded-md border border-border bg-background p-0.5">
                    {SWITCHABLE_UNITS.map((u) => (
                      <button
                        key={u}
                        type="button"
                        onClick={() => {
                          if (u === displayUnit) return
                          // Convert current value to new unit
                          const currentVal = parseFloat(lengthInput) || 0
                          if (currentVal > 0) {
                            const converted = convertUnit(
                              currentVal,
                              displayUnit,
                              u
                            )
                            setLengthInput(
                              converted % 1 === 0
                                ? converted.toString()
                                : converted.toFixed(2)
                            )
                          }
                          setDisplayUnit(u)
                          setLengthError(null)
                        }}
                        className={[
                          'px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wide transition-colors',
                          displayUnit === u
                            ? 'bg-accent text-accent-foreground'
                            : 'text-muted hover:bg-surface hover:text-foreground'
                        ].join(' ')}
                        aria-pressed={displayUnit === u}
                      >
                        {formatUnit(u)}
                      </button>
                    ))}
                  </div>
                </div>
                {/* The field carries its own -/+ rather than leaning on the
                    browser's number spinner. Hiding that spinner takes a
                    different rule per engine (the webkit pseudo-elements do
                    nothing in Firefox, which needs appearance:textfield), and
                    where it did render it sat on top of the unit suffix. An
                    explicit stepper also matches Pieces and Quantity below,
                    which already have one. */}
                <div className="flex items-center gap-2">
                  <StepperButton
                    direction="decrease"
                    onClick={() => stepLength(-1)}
                    disabled={
                      convertUnit(lengthValue, displayUnit, nativeUnit) <= minOrder
                    }
                    aria-label={`Decrease length by ${incrementStep > 0 ? incrementStep : 1} ${formatUnit(nativeUnit)}`}
                  />
                  <div className="relative flex-1">
                    <input
                      type="number"
                      min={0}
                      step={incrementStep > 0 ? convertUnit(incrementStep, nativeUnit, displayUnit) : 0.5}
                      value={lengthInput}
                      onChange={(e) => {
                        setLengthInput(e.target.value)
                        setLengthError(null)
                      }}
                      onBlur={handleLengthBlur}
                      className={[
                        'w-full bg-background border rounded-md pl-3 pr-10 py-2.5 text-sm text-foreground outline-none transition-colors',
                        '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0',
                        lengthError
                          ? 'border-danger focus:border-danger'
                          : 'border-border focus:border-[var(--focus)]'
                      ].join(' ')}
                      aria-label={`Length in ${formatUnit(displayUnit)}`}
                      aria-invalid={!!lengthError}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted">
                      {formatUnit(displayUnit)}
                    </span>
                  </div>
                  <StepperButton
                    direction="increase"
                    onClick={() => stepLength(1)}
                    disabled={
                      !!maxOrder &&
                      convertUnit(lengthValue, displayUnit, nativeUnit) >= maxOrder
                    }
                    aria-label={`Increase length by ${incrementStep > 0 ? incrementStep : 1} ${formatUnit(nativeUnit)}`}
                  />
                </div>
                {lengthError && (
                  <p className="mt-1 text-xs text-danger">{lengthError}</p>
                )}
              </div>

              {/* Pieces stepper */}
              <div>
                <label className="text-xs font-semibold text-muted uppercase tracking-wide mb-1.5 block">
                  Pieces
                </label>
                <div className="flex items-center gap-3">
                  <StepperButton
                    direction="decrease"
                    onClick={() => setPieces((p) => Math.max(1, p - 1))}
                    disabled={pieces <= 1}
                    aria-label="Decrease pieces"
                  />
                  <span className="text-sm font-bold text-foreground min-w-[2rem] text-center">
                    {pieces}
                  </span>
                  <StepperButton
                    direction="increase"
                    onClick={() => setPieces((p) => Math.min(99, p + 1))}
                    disabled={pieces >= 99}
                    aria-label="Increase pieces"
                  />
                </div>
              </div>
            </>
          )}

          {purchaseMode === 'full-roll' && hasRollOption && (
            <>
              {/* Roll info */}
              <div className="bg-background rounded-md px-4 py-3">
                <span className="text-sm text-muted">
                  {rollLengthInDisplay
                    ? `${rollLengthInDisplay % 1 === 0 ? rollLengthInDisplay : rollLengthInDisplay.toFixed(1)} ${formatUnit(displayUnit)} roll`
                    : 'Full roll'}
                </span>
                {rollSavingsPercent !== null && rollSavingsPercent > 0 && (
                  <span className="ml-2 text-xs font-semibold text-success">
                    Save {rollSavingsPercent}% vs. custom cut
                  </span>
                )}
              </div>

              {/* Roll quantity stepper */}
              <div>
                <label className="text-xs font-semibold text-muted uppercase tracking-wide mb-1.5 block">
                  Quantity
                </label>
                <div className="flex items-center gap-3">
                  <StepperButton
                    direction="decrease"
                    onClick={() => setRollQuantity((q) => Math.max(1, q - 1))}
                    disabled={rollQuantity <= 1}
                    aria-label="Decrease quantity"
                  />
                  <span className="text-sm font-bold text-foreground min-w-[2rem] text-center">
                    {rollQuantity}
                  </span>
                  <StepperButton
                    direction="increase"
                    onClick={() => setRollQuantity((q) => Math.min(99, q + 1))}
                    disabled={rollQuantity >= 99}
                    aria-label="Increase quantity"
                  />
                </div>
              </div>
            </>
          )}

          {/* Note */}
          <div>
            <label className="text-xs font-semibold text-muted uppercase tracking-wide mb-1.5 block">
              Note{' '}
              <span className="font-normal normal-case text-muted">
                (optional)
              </span>
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Ford Transit #4"
              className="w-full bg-background border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder:text-muted outline-none focus:border-[var(--focus)] transition-colors"
              aria-label="Order note"
            />
          </div>
        </div>

        {/* Footer — price breakdown + button */}
        <div className="px-5 py-4 border-t border-border">
          {/* Price breakdown */}
          {purchaseMode === 'custom-cut' && pricePerDisplayUnit > 0 && (
            <p className="text-xs text-muted mb-3 tabular-nums">
              {lengthValue > 0 ? lengthValue : 0} {formatUnit(displayUnit)}{' '}
              {pieces > 1 ? `× ${pieces} pcs ` : ''}× ${pricePerDisplayUnit.toFixed(2)}/{formatUnit(displayUnit)}
            </p>
          )}
          {purchaseMode === 'full-roll' &&
            variantPricing?.rollPrice &&
            rollQuantity > 1 && (
              <p className="text-xs text-muted mb-3 tabular-nums">
                ${variantPricing.rollPrice.toFixed(2)} × {rollQuantity} rolls
              </p>
            )}

          {isOutOfStock ? (
            <div className="text-center py-2">
              <span className="text-sm font-semibold text-danger">
                Out of Stock
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={
                isOutOfStock ||
                (purchaseMode === 'custom-cut' && lengthValue <= 0)
              }
              className="w-full py-3 rounded-md text-sm font-bold text-accent-foreground bg-accent hover:bg-[var(--btn-primary-bg-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed tabular-nums"
            >
              Add to Cart: ${total.toFixed(2)}
            </button>
          )}
        </div>
      </div>
    </div>
  )

  // Render via portal
  if (typeof window === 'undefined') return null
  return createPortal(modalContent, document.body)
}
